// TODO: need to set this component to be global component

import { flexRender, Row, Table as TableType } from "@tanstack/react-table";

import { ScrollArea } from "@/components//ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { DatasetExploreItem } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

interface TableDataProps<TData = DatasetExploreItem> {
  readonly table: TableType<TData>;
  readonly getHeaderWidth?: (headerId: string) => string;
  /**
   * Rows drawn above the real ones, for datasets the list response has yet to
   * carry. Kept out of the table's data so pagination totals, sorting and row
   * ids stay untouched.
   */
  readonly pendingRows?: Row<TData>[];
  /** Placeholders for datasets known to exist but with no details yet. */
  readonly skeletonRowCount?: number;
}

const defaultHeaderWidth = (headerId: string): string => {
  switch (headerId) {
    case "name":
      return "w-[350px]";
    case "origin":
      return "w-[110px]";
    case "total_rows":
      return "w-[110px]";
    case "country_id":
      return "w-[150px]";
    case "spatial_aggregation_type":
      return "w-[180px]";
    case "published":
      return "w-[120px]";
    case "created_by":
      return "w-[200px]";
    case "created_at":
      return "w-[150px]";
    case "updated_by":
      return "w-[200px]";
    case "updated_at":
      return "w-[150px]";
    case "size_kb":
      return "w-[120px]";
    case "id":
      return "w-[80px]";
    default:
      return "";
  }
};

export default function TableData<TData = DatasetExploreItem>({
  table,
  getHeaderWidth = defaultHeaderWidth,
  pendingRows = [],
  skeletonRowCount = 0
}: TableDataProps<TData>) {
  const hasRows = table.getRowModel().rows?.length > 0;
  // Same source as the empty-state colSpan, so a skeleton spans every column.
  const columnCount = table.getAllColumns().length;

  // Shared by pending and real rows, so there is one copy of the cell markup.
  const renderRow = (row: Row<TData>, isLastRow: boolean) => (
    <TableRow
      key={row.id}
      data-state={row.getIsSelected() && "selected"}
      className="border-b-0"
    >
      {row.getVisibleCells().map((cell) => {
        const content = flexRender(
          cell.column.columnDef.cell,
          cell.getContext()
        );
        const isFirstCell = row.getVisibleCells()[0]?.id === cell.id;
        const isLastCell =
          row.getVisibleCells()[row.getVisibleCells().length - 1]?.id ===
          cell.id;

        return (
          <TableCell
            key={cell.id}
            className={twMerge(
              "py-2 px-3 !border-b",
              isFirstCell && "!border-l",
              isLastCell && "!border-r",
              "p-0 max-w-[200px] py-2 px-3",
              isLastRow && isFirstCell && "rounded-bl-lg",
              isLastRow && isLastCell && "rounded-br-lg"
            )}
          >
            {content}
          </TableCell>
        );
      })}
    </TableRow>
  );

  return (
    <ScrollArea className="h-full">
      <Table className="relative border-separate table-fixed border-spacing-0">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="border-b-0">
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className={twMerge(
                      "bg-white-smoke py-2 px-3 !border-t !border-b !border-r",
                      getHeaderWidth(header.id),
                      header.index === 0 && "!border-l rounded-tl-lg",
                      header.index === headerGroup.headers.length - 1 &&
                        "rounded-tr-lg"
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {Array.from({ length: skeletonRowCount }, (_, skeletonIndex) => (
            <TableRow
              key={`skeleton-${skeletonIndex}`}
              className="border-b-0"
              data-testid="dataset-skeleton-row"
            >
              {Array.from({ length: columnCount }, (_, cellIndex) => (
                <TableCell
                  key={cellIndex}
                  className={twMerge(
                    "py-2 px-3 !border-b",
                    cellIndex === 0 && "!border-l",
                    cellIndex === columnCount - 1 && "!border-r"
                  )}
                >
                  <div className="h-3 rounded bg-neutral-100 animate-pulse" />
                </TableCell>
              ))}
            </TableRow>
          ))}
          {pendingRows.map((row) => renderRow(row, !hasRows))}
          {hasRows
            ? table
                .getRowModel()
                .rows.map((row, rowIndex) =>
                  renderRow(
                    row,
                    rowIndex === table.getRowModel().rows.length - 1
                  )
                )
            : // A pending row already says something is coming.
              pendingRows.length === 0 &&
              skeletonRowCount === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllColumns().length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
        </TableBody>
      </Table>
    </ScrollArea>
  );
}
